import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_CLICK_ID_STRUCTURE,
  DEFAULT_DOMAIN_SCHEME,
  type ShortlinkClickIdStructure,
  type ShortlinkDomainScheme,
} from '@trafficpulse/shared';

import { useLeadsUsage } from '@/hooks';
import { buildTitleBase } from '@/features/smswiser-campaigns/utils/dateFormat';
import { getDefaultTldValue } from '@/features/smswiser-campaigns/utils/tldDefaults';

import { ConfigurationColumn, CreativeColumn, LeadsColumn } from '../columns';
import { DraftCreatorFooter } from './DraftCreatorFooter';
import type { PrepareNewDropsFormProps } from '../shared/PrepareNewDropsForm.types';
import type { AiTextMetadataPayload } from '@/hooks';
import {
  MAX_SELECTED_TEXTS,
  MAX_TEXTS,
  buildRecentLeadsStorageKey,
  filterSenderNamesMax11,
  parseRecentIds,
  uniqueKeepOrderStrings,
} from '../shared/PrepareNewDropsForm.utils';

export const PrepareNewDropsForm: FC<PrepareNewDropsFormProps> = ({
  countryKey,
  campaigns,
  wiserId,
  workDate,
  titlePrefix,
  campaignName,
  gateways,
  tlds,
  leadsLists,
  tagColors,
  tagsByTextId,
  tagsByGatewayId,
  tagsLoading,
  tagFilterOptions: tagFilterOptionsProp,
  creative,
  isBusy,
  onSelectCampaign,
  onCreateDrafts,
  onUpdateCreative,
  onDeleteSender,
  onDeleteSenders,
  onDeleteText,
  onDeleteTextsBulk,
}) => {
  // State Initialization - default to .com when available
  const [routeId, setRouteId] = useState<number>(0);
  const [tldValue, setTldValue] = useState<string>('');
  const [amount, setAmount] = useState<number | ''>(150);
  const [executeAfter, setExecuteAfter] = useState<string>('');
  const [forceSend, setForceSend] = useState(false);
  const [sendInBatches, setSendInBatches] = useState(false);
  const [batchAmount, setBatchAmount] = useState<number | undefined>(undefined);
  const [batchDelay, setBatchDelay] = useState<number | undefined>(undefined);
  const [aiOptimizationEnabled, setAiOptimizationEnabled] = useState(false);
  const [aiOptimizationType, setAiOptimizationType] = useState<'ROI' | 'CTR' | ''>('');
  const [aiOptimizationBatchAmount, setAiOptimizationBatchAmount] = useState<number | ''>('');
  const [aiOptimizationMinimalValuePercent, setAiOptimizationMinimalValuePercent] = useState<number | ''>('');
  const [aiOptimizationSendUntil, setAiOptimizationSendUntil] = useState<string>('');
  const [useOnlyNewDomains, setUseOnlyNewDomains] = useState(false);
  const [clickIdStructure, setClickIdStructure] =
    useState<ShortlinkClickIdStructure>(DEFAULT_CLICK_ID_STRUCTURE);
  const [domainScheme, setDomainScheme] = useState<ShortlinkDomainScheme>(DEFAULT_DOMAIN_SCHEME);
  const [staticTagId, setStaticTagId] = useState<string | null>(null);

  const [senderSearch, setSenderSearch] = useState('');
  const [textSearch, setTextSearch] = useState('');
  
  // Creative State (campaign library + selection for this batch)
  const [senderNames, setSenderNames] = useState<string[]>(() => creative.senderNames ?? []);
  const [smsTexts, setSmsTexts] = useState<string[]>(() => creative.smsTexts ?? []);
  const [selectedSenderNames, setSelectedSenderNames] = useState<string[]>(() => creative.senderNames ?? []);
  const [selectedSmsTexts, setSelectedSmsTexts] = useState<string[]>(() => (creative.smsTexts ?? []).slice(0, MAX_SELECTED_TEXTS));
  const [newSenderValue, setNewSenderValue] = useState('');
  const [newTextValue, setNewTextValue] = useState('');
  const [newTextError, setNewTextError] = useState<string | null>(null);
  const [pendingHideTexts, setPendingHideTexts] = useState<string[]>([]);
  const [pendingHideSenders, setPendingHideSenders] = useState<string[]>([]);
  /** Per-drop SMS texts from AI Use & Hide. Index matches selectedLeadIds. Cleared when lists change. */
  const [perDropSmsTexts, setPerDropSmsTexts] = useState<string[][] | null>(null);
  /** Per-drop sender names from AI Use & Hide. Index matches selectedLeadIds. */
  const [perDropSenderNames, setPerDropSenderNames] = useState<string[][] | null>(null);

  // Leads State
  const [leadSearch, setLeadSearch] = useState('');
  const [leadTagFilter, setLeadTagFilter] = useState('');
  const [selectedLeadIds, setSelectedLeadIds] = useState<number[]>([]);
  const [recentLeadIds, setRecentLeadIds] = useState<number[]>([]);

  const recentStorageKey = useMemo(() => buildRecentLeadsStorageKey({ countryKey, wiserId }), [countryKey, wiserId]);
  const usageQuery = useLeadsUsage(countryKey, workDate);
  const usageByListId = usageQuery.data ?? {};

  // --- Effects ---
  useEffect(() => {
    setRecentLeadIds(parseRecentIds(localStorage.getItem(recentStorageKey)));
  }, [recentStorageKey]);

  useEffect(() => {
    if (!tldValue && tlds.length > 0) {
      const def = getDefaultTldValue(tlds);
      if (def) setTldValue(def);
    }
  }, [tlds, tldValue]);

  useEffect(() => {
    const nextSenders = creative.senderNames ?? [];
    const nextTexts = creative.smsTexts ?? [];
    setSenderNames(nextSenders);
    setSmsTexts(nextTexts);
    setSelectedSenderNames((prev) => (prev.length ? prev.filter((s) => nextSenders.includes(s)) : nextSenders));
    setSelectedSmsTexts((prev) => {
      const kept = prev.filter((t) => nextTexts.includes(t));
      if (prev.length > 0 && kept.length === 0) return prev;
      return prev.length ? kept.slice(0, MAX_SELECTED_TEXTS) : nextTexts.slice(0, MAX_SELECTED_TEXTS);
    });
  }, [creative.senderNames, creative.smsTexts]);

  useEffect(() => {
    setPerDropSmsTexts((prev) =>
      prev && selectedLeadIds.length === prev.length ? prev : null,
    );
  }, [selectedLeadIds.length]);
  useEffect(() => {
    setPerDropSenderNames((prev) =>
      prev && selectedLeadIds.length === prev.length ? prev : null,
    );
  }, [selectedLeadIds.length]);

  // --- Computed ---
  const titleBase = useMemo(() => buildTitleBase(titlePrefix || campaignName || 'DRAFT', workDate), [campaignName, titlePrefix, workDate]);

  const selectedSet = useMemo(() => new Set(selectedLeadIds), [selectedLeadIds]);

  const dropLabels = useMemo(
    () =>
      selectedLeadIds.length > 1
        ? selectedLeadIds.map((id) => {
            const list = leadsLists.find((l) => l.id === id);
            return { id: String(id), label: list?.name ?? `List ${id}` };
          })
        : undefined,
    [selectedLeadIds, leadsLists],
  );

  const availableTags = useMemo(() => {
    if (tagFilterOptionsProp && tagFilterOptionsProp.length > 0) {
      return tagFilterOptionsProp;
    }
    const set = new Set<string>(['New', 'Unused']);
    for (const l of leadsLists) {
      const tags = l.performanceTags ?? [];
      for (const t of tags) if (t && String(t).trim()) set.add(String(t).trim());
    }
    return [...set].sort();
  }, [leadsLists, tagFilterOptionsProp]);

  const filteredLeads = useMemo(() => {
    const q = leadSearch.trim().toLowerCase();
    let list = q ? leadsLists.filter((l) => l.name.toLowerCase().includes(q)) : leadsLists;
    if (leadTagFilter) {
      if (leadTagFilter === '(none)') {
        list = list.filter((l) => !(l.performanceTags?.length));
      } else {
        const q = leadTagFilter.toLowerCase();
        list = list.filter((l) =>
          l.performanceTags?.some((t) => t.toLowerCase() === q),
        );
      }
    }
    return [...list].sort((a, b) => {
      const aSel = selectedSet.has(a.id);
      const bSel = selectedSet.has(b.id);
      return aSel === bSel ? a.name.localeCompare(b.name) : aSel ? -1 : 1;
    });
  }, [leadSearch, leadTagFilter, leadsLists, selectedSet]);

  const aiOptIncomplete =
    aiOptimizationEnabled &&
    (!aiOptimizationType || aiOptimizationBatchAmount === '' || aiOptimizationMinimalValuePercent === '' || aiOptimizationSendUntil === '');

  const batchesIncomplete = sendInBatches === true && (!batchAmount || !batchDelay);

  const hasValidSmsTexts =
    perDropSmsTexts != null && perDropSmsTexts.length === selectedLeadIds.length
      ? perDropSmsTexts.every((batch) => batch.length > 0 && batch.length <= MAX_SELECTED_TEXTS)
      : selectedSmsTexts.length > 0 && selectedSmsTexts.length <= MAX_SELECTED_TEXTS;
  const hasValidSenders =
    perDropSenderNames != null && perDropSenderNames.length === selectedLeadIds.length
      ? perDropSenderNames.every((batch) => batch.length > 0)
      : selectedSenderNames.length > 0;
  const canCreate =
    wiserId > 0 &&
    routeId > 0 &&
    Boolean(tldValue) &&
    amount !== '' &&
    amount >= 1 &&
    selectedLeadIds.length > 0 &&
    hasValidSenders &&
    hasValidSmsTexts &&
    !aiOptIncomplete &&
    !batchesIncomplete;

  const missingRequirements = useMemo(() => {
    const missing: string[] = [];
    if (!wiserId || wiserId <= 0) missing.push('Campaign');
    if (!routeId || routeId <= 0) missing.push('Route/gateway');
    if (!tldValue) missing.push('TLD');
    if (amount === '' || amount < 1) missing.push('Amount (min 1)');
    if (selectedLeadIds.length === 0) missing.push('At least 1 leads list');
    if (perDropSenderNames != null && perDropSenderNames.length === selectedLeadIds.length) {
      const invalid = perDropSenderNames.some((b) => b.length === 0);
      if (invalid) missing.push('Each list needs at least 1 sender (per-drop AI)');
    } else if (selectedSenderNames.length === 0) {
      missing.push('At least 1 sender');
    }
    if (perDropSmsTexts != null && perDropSmsTexts.length === selectedLeadIds.length) {
      const invalid = perDropSmsTexts.some((b) => b.length === 0 || b.length > MAX_SELECTED_TEXTS);
      if (invalid) missing.push('Each list needs 1–15 SMS texts (per-drop AI)');
    } else {
      if (selectedSmsTexts.length === 0) missing.push('At least 1 SMS text');
      if (selectedSmsTexts.length > MAX_SELECTED_TEXTS) missing.push(`Max ${MAX_SELECTED_TEXTS} SMS texts selected`);
    }
    if (aiOptIncomplete) missing.push('AI Optimization: Type, Batch amount, Minimal %, Send until');
    if (batchesIncomplete) missing.push('Send in batches: Batch amount, Batch delay');
    return missing;
  }, [wiserId, routeId, tldValue, amount, selectedLeadIds.length, selectedSenderNames.length, selectedSmsTexts.length, perDropSmsTexts, perDropSenderNames, aiOptIncomplete, batchesIncomplete]);

  // --- Cost Calculation ---
  const selectedGateway = useMemo(() => gateways.find(g => g.id === routeId), [gateways, routeId]);
  
  const estimatedCost = useMemo(() => {
    const price = selectedGateway?.pricePerSms || 0;
    const amt = amount === '' || amount < 1 ? 0 : amount;
    const total = price * amt;
    return total >= 1 ? total.toFixed(2) : total >= 0.01 ? total.toFixed(3) : total.toFixed(4);
  }, [selectedGateway, amount]);

  // --- Handlers ---
  const handleCreate = async () => {
    if (!canCreate) return;
    
    // Update recents
    const updatedRecents = uniqueKeepOrderStrings([...selectedLeadIds.map(String), ...recentLeadIds.map(String)])
      .slice(0, 12).map(Number);
    setRecentLeadIds(updatedRecents);
    localStorage.setItem(recentStorageKey, JSON.stringify(updatedRecents));

    const aiOpt =
      aiOptimizationEnabled &&
      aiOptimizationType &&
      aiOptimizationBatchAmount !== '' &&
      aiOptimizationMinimalValuePercent !== '' &&
      aiOptimizationSendUntil !== '';
    const smsTextsForPayload =
      perDropSmsTexts != null && perDropSmsTexts.length === selectedLeadIds.length
        ? perDropSmsTexts.flat()
        : uniqueKeepOrderStrings(selectedSmsTexts);
    const senderNamesForPayload =
      perDropSenderNames != null && perDropSenderNames.length === selectedLeadIds.length
        ? perDropSenderNames.flat()
        : filterSenderNamesMax11(uniqueKeepOrderStrings(selectedSenderNames));
    await onCreateDrafts({
      routeId,
      shortLinkTldValue: tldValue,
      amount: amount as number,
      executeAfter: executeAfter.trim() || undefined,
      senderNames: senderNamesForPayload,
      smsTexts: smsTextsForPayload,
      smsTextsPerDrop:
        perDropSmsTexts != null && perDropSmsTexts.length === selectedLeadIds.length
          ? perDropSmsTexts
          : undefined,
      senderNamesPerDrop:
        perDropSenderNames != null && perDropSenderNames.length === selectedLeadIds.length
          ? perDropSenderNames
          : undefined,
      rotators: creative.rotators ?? null,
      leadsListWiserIds: selectedLeadIds,
      forceSend,
      sendInBatches,
      batchAmount,
      batchDelay,
      aiOptimizationEnabled: aiOpt ? true : false,
      aiOptimizationType: aiOpt ? (aiOptimizationType as 'ROI' | 'CTR') : undefined,
      aiOptimizationBatchAmount: aiOpt ? (aiOptimizationBatchAmount as number) : undefined,
      aiOptimizationMinimalValuePercent: aiOpt ? (aiOptimizationMinimalValuePercent as number) : undefined,
      aiOptimizationSendUntil: aiOpt && aiOptimizationSendUntil ? aiOptimizationSendUntil : undefined,
      useOnlyNewDomains,
      clickIdStructure,
      domainScheme,
      staticTagId: staticTagId ?? undefined,
    });

    if (pendingHideTexts.length > 0) {
      const hideSet = new Set(pendingHideTexts);
      if (onDeleteTextsBulk) await onDeleteTextsBulk(pendingHideTexts);
      else for (const t of pendingHideTexts) await onDeleteText(t);
      setSmsTexts((prev) => prev.filter((t) => !hideSet.has(t)));
      setSelectedSmsTexts((prev) => prev.filter((t) => !hideSet.has(t)));
      setPendingHideTexts([]);
    }
    if (pendingHideSenders.length > 0) {
      if (onDeleteSenders) await onDeleteSenders(pendingHideSenders);
      else for (const name of pendingHideSenders) await onDeleteSender(name);
      const hideSet = new Set(pendingHideSenders);
      setSenderNames((prev) => prev.filter((s) => !hideSet.has(s)));
      setSelectedSenderNames((prev) => prev.filter((s) => !hideSet.has(s)));
      setPendingHideSenders([]);
    }
    setSelectedLeadIds([]);
  };

  const handleAddSender = async (senderName: string) => {
    if (senderName.length > 11) return;
    const next = filterSenderNamesMax11(
      uniqueKeepOrderStrings([...senderNames, senderName]).slice(0, 50),
    );
    setSenderNames(next);
    setSelectedSenderNames((prev) =>
      uniqueKeepOrderStrings([...prev, senderName]).filter((s) => next.includes(s)),
    );
    await onUpdateCreative({ senderNames: next });
  };

  const handleAddSenders = async (senders: string[]) => {
    const valid = filterSenderNamesMax11(senders);
    const merged = filterSenderNamesMax11(
      uniqueKeepOrderStrings([...senderNames, ...valid]).slice(0, 50),
    );
    setSenderNames(merged);
    setSelectedSenderNames((prev) =>
      uniqueKeepOrderStrings([...prev, ...valid]).filter((s) => merged.includes(s)),
    );
    await onUpdateCreative({ senderNames: merged });
  };

  const handleAddTexts = async (texts: string[], aiTextMetadata?: AiTextMetadataPayload[]) => {
    const newSet = new Set(texts);
    const existingWithoutNew = smsTexts.filter((t) => !newSet.has(t));
    const merged = uniqueKeepOrderStrings([...texts, ...existingWithoutNew]).slice(0, MAX_TEXTS);
    setSmsTexts(merged);
    setSelectedSmsTexts((prev) =>
      uniqueKeepOrderStrings([...prev, ...texts]).filter((t) => merged.includes(t)).slice(0, MAX_SELECTED_TEXTS),
    );
    await onUpdateCreative({ smsTexts: merged, aiTextMetadata });
  };

  /** Adds texts without selecting (used when Save/Use & Hide from AI generated). */
  const handleAddTextsDeselected = async (texts: string[], aiTextMetadata?: AiTextMetadataPayload[]) => {
    const newSet = new Set(texts);
    const existingWithoutNew = smsTexts.filter((t) => !newSet.has(t));
    const merged = uniqueKeepOrderStrings([...texts, ...existingWithoutNew]).slice(0, MAX_TEXTS);
    setSmsTexts(merged);
    await onUpdateCreative({ smsTexts: merged, aiTextMetadata });
  };

  /** Selects ONLY the given texts (used after AI Apply). Adds to smsTexts so selection persists before onAddTexts completes. */
  const handleSaveGeneratedAndSelectOnly = (texts: string[]) => {
    const newSet = new Set(texts);
    const existingWithoutNew = smsTexts.filter((t) => !newSet.has(t));
    const merged = uniqueKeepOrderStrings([...texts, ...existingWithoutNew]).slice(0, MAX_TEXTS);
    setSmsTexts(merged);
    setSelectedSmsTexts(texts.slice(0, MAX_SELECTED_TEXTS));
  };

  /** Selects ONLY the given senders (used after AI Apply). */
  const handleSaveGeneratedSendersAndSelectOnly = (senders: string[]) => {
    const valid = filterSenderNamesMax11(senders);
    const merged = filterSenderNamesMax11(uniqueKeepOrderStrings([...senderNames, ...valid]).slice(0, 50));
    setSenderNames(merged);
    setSelectedSenderNames(valid.filter((s) => merged.includes(s)));
  };

  const toggleLead = (id: number) => {
    setSelectedLeadIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handlePerDropSmsTextsApplied = useCallback((batches: string[][]) => setPerDropSmsTexts(batches), []);
  const handleAiGeneratedDeselectOld = useCallback(() => setSelectedSmsTexts([]), []);
  const handleMarkTextsForHide = useCallback((texts: string[]) => setPendingHideTexts((p) => [...p, ...texts]), []);
  const handlePerDropSendersApplied = useCallback((batches: string[][]) => setPerDropSenderNames(batches), []);
  const handleAiGeneratedSendersDeselectOld = useCallback(() => setSelectedSenderNames([]), []);
  const handleMarkSendersForHide = useCallback((senders: string[]) => setPendingHideSenders((p) => [...p, ...senders]), []);

  return (
    <div className="draft-form-container flex h-full min-h-0 min-w-0 flex-col bg-background">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden xl:flex-row xl:overflow-y-hidden">

      <ConfigurationColumn
        titleBase={titleBase}
        campaigns={campaigns}
        campaignId={wiserId}
        onCampaignChange={onSelectCampaign}
        countryKey={countryKey}
        gateways={gateways}
        tagsByGatewayId={tagsByGatewayId}
        tagColors={tagColors}
        tlds={tlds}
        routeId={routeId}
        setRouteId={setRouteId}
        tldValue={tldValue}
        setTldValue={setTldValue}
        amount={amount}
        setAmount={setAmount}
        executeAfter={executeAfter}
        setExecuteAfter={setExecuteAfter}
        estimatedCost={estimatedCost}
        isBusy={isBusy}
        forceSend={forceSend}
        setForceSend={setForceSend}
        sendInBatches={sendInBatches}
        setSendInBatches={setSendInBatches}
        batchAmount={batchAmount}
        setBatchAmount={setBatchAmount}
        batchDelay={batchDelay}
        setBatchDelay={setBatchDelay}
        aiOptimizationEnabled={aiOptimizationEnabled}
        setAiOptimizationEnabled={setAiOptimizationEnabled}
        aiOptimizationType={aiOptimizationType}
        setAiOptimizationType={setAiOptimizationType}
        aiOptimizationBatchAmount={aiOptimizationBatchAmount}
        setAiOptimizationBatchAmount={setAiOptimizationBatchAmount}
        aiOptimizationMinimalValuePercent={aiOptimizationMinimalValuePercent}
        setAiOptimizationMinimalValuePercent={setAiOptimizationMinimalValuePercent}
        aiOptimizationSendUntil={aiOptimizationSendUntil}
        setAiOptimizationSendUntil={setAiOptimizationSendUntil}
        useOnlyNewDomains={useOnlyNewDomains}
        setUseOnlyNewDomains={setUseOnlyNewDomains}
        clickIdStructure={clickIdStructure}
        setClickIdStructure={setClickIdStructure}
        domainScheme={domainScheme}
        setDomainScheme={setDomainScheme}
        staticTagId={staticTagId}
        setStaticTagId={setStaticTagId}
      />

      <CreativeColumn
        countryKey={countryKey}
        routeId={routeId}
        senderNames={senderNames}
        setSenderNames={setSenderNames}
        smsTexts={smsTexts}
        smsTextsWithIds={creative.smsTextsWithIds}
        setSmsTexts={setSmsTexts}
        selectedSenderNames={selectedSenderNames}
        setSelectedSenderNames={setSelectedSenderNames}
        selectedSmsTexts={selectedSmsTexts}
        setSelectedSmsTexts={setSelectedSmsTexts}
        senderSearch={senderSearch}
        setSenderSearch={setSenderSearch}
        textSearch={textSearch}
        setTextSearch={setTextSearch}
        newSenderValue={newSenderValue}
        setNewSenderValue={setNewSenderValue}
        newTextValue={newTextValue}
        setNewTextValue={setNewTextValue}
        newTextError={newTextError}
        setNewTextError={setNewTextError}
        onAddSender={handleAddSender}
        onAddSenders={handleAddSenders}
        onAddTexts={handleAddTexts}
        onAddTextsDeselected={handleAddTextsDeselected}
        onSaveGeneratedAndSelectOnly={handleSaveGeneratedAndSelectOnly}
        onPerDropSmsTextsApplied={handlePerDropSmsTextsApplied}
        onAiGeneratedDeselectOld={handleAiGeneratedDeselectOld}
        onDeleteSender={onDeleteSender}
        onDeleteSenders={onDeleteSenders}
        onDeleteText={onDeleteText}
        onDeleteTextsBulk={onDeleteTextsBulk}
        onMarkTextsForHide={handleMarkTextsForHide}
        pendingHideTexts={pendingHideTexts}
        onSaveGeneratedSendersAndSelectOnly={handleSaveGeneratedSendersAndSelectOnly}
        onPerDropSendersApplied={handlePerDropSendersApplied}
        onAiGeneratedSendersDeselectOld={handleAiGeneratedSendersDeselectOld}
        onMarkSendersForHide={handleMarkSendersForHide}
        pendingHideSenders={pendingHideSenders}
        maxTexts={MAX_TEXTS}
        tagsByTextId={tagsByTextId}
        tagColors={tagColors}
        isBusy={isBusy}
        dropCount={selectedLeadIds.length}
        dropLabels={dropLabels}
      />

      <LeadsColumn
        leadsLists={leadsLists}
        tagColors={tagColors}
        tagsLoading={tagsLoading}
        leadTagFilter={leadTagFilter}
        setLeadTagFilter={setLeadTagFilter}
        availableTags={availableTags}
        usageByListId={usageByListId}
        leadSearch={leadSearch}
        setLeadSearch={setLeadSearch}
        filteredLeads={filteredLeads}
        selectedLeadIds={selectedLeadIds}
        setSelectedLeadIds={setSelectedLeadIds}
        selectedSet={selectedSet}
        recentLeadIds={recentLeadIds}
        toggleLead={toggleLead}
      />
      </div>
      <DraftCreatorFooter
        canCreate={canCreate}
        missingRequirements={missingRequirements}
        estimatedCost={estimatedCost}
        isBusy={isBusy}
        selectedCount={selectedLeadIds.length}
        tldValue={tldValue}
        onCreate={handleCreate}
      />
    </div>
  );
};

export default PrepareNewDropsForm;